import { useQuery } from "@tanstack/react-query"

import type { QueryFunctionContext } from "@tanstack/react-query"
import type { SocialUser } from "@/types/djangio"

import * as notifications from "@/components/SocialMediaInput/notifications"

type FilterKey =
    | "custom_persons__contains"
    | "supporters__contains"
    | "public_organizations__contains"
    | "person"
    | "committee"
type RelatedFieldKey = "public_organization_id" | "supporter_id" | "person_id"

const SocialUserModel = DjangIO.app.social.models.SocialUser

// queryKey factory! Use this to create query keys for react-query and infer the types.
// queryKeys are always arrays, but by adding our "dependencies" as an object,
// we can destructure by name in our queryFn
export const socialUserKeys = {
    all: [{ _model: "social_users" }] as const,
    detail: ({ entityId, filterKey }: { entityId: string; filterKey: FilterKey }) =>
        [{ ...socialUserKeys.all[0], entityId, filterKey }] as const,
}

const fetchSocialUsers = ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof socialUserKeys)["detail"]>>): Promise<{ objects: SocialUser[] }> => {
    // _model is also available here, as would any piece of the queryKey added in the future!
    const [{ entityId, filterKey }] = queryKey
    const filter = {
        [filterKey]: entityId,
    }

    return SocialUserModel.objects.filter(filter).GET()
}

export const useSocialUsers = (entityId: string, filterKey: FilterKey) =>
    useQuery({
        queryKey: socialUserKeys.detail({ entityId, filterKey }),
        queryFn: fetchSocialUsers,
        enabled: Boolean(entityId),
        // TODO: We don't want to refetch while the user is editing the form,
        // but we do need to refetch if the user navigates away and then comes back
        // staleTime: Infinity,
        placeholderData: { objects: [] },
        select: (data) => {
            const socialUsersByType = data.objects.reduce((socialUsers, socialUser: SocialUser) => {
                const userType = socialUser.social_user_type

                if (socialUsers[userType]) {
                    return {
                        ...socialUsers,
                        [userType]: [...socialUsers[userType], socialUser],
                    }
                }

                return { ...socialUsers, [userType]: [socialUser] }
            }, {})

            return socialUsersByType
        },
    })

export const updateSocialUsers = ({
    quorumDataTypeValue,
    newAccounts,
    relatedFieldKey,
    entityId,
    toDelete,
}: {
    quorumDataTypeValue: number
    newAccounts: { [userType: number]: Partial<SocialUser>[] }
    relatedFieldKey: RelatedFieldKey
    entityId: number
    toDelete: number[]
}) => {
    const newSocialUsers = Object.keys(newAccounts).reduce((acc, userType) => {
        const formattedAccounts = newAccounts[userType].map((account) => ({
            [relatedFieldKey]: entityId,
            social_user_type: parseInt(userType),
            userid: account.socialUserId,
            username: account.username,
        }))

        return [...acc, ...formattedAccounts]
    }, [])

    const addUsers = () => {
        if (newSocialUsers.length > 0) {
            const onSuccess = (response) => {
                const errors = response.objects.reduce((errorMessages, socialUser) => {
                    if (socialUser.error) {
                        return [...errorMessages, socialUser.error]
                    }

                    return errorMessages
                }, [])

                if (errors.length) {
                    notifications.errors(errors.join(" "))
                }
            }

            const onFailure = () => {
                notifications.failure()
            }

            return SocialUserModel.objects
                .bulk_update({
                    objects: newSocialUsers,
                })
                .PATCH()
                .then(onSuccess)
                .catch(onFailure)
        }

        return Promise.resolve({})
    }

    const deleteUsers = () => {
        if (toDelete.length > 0) {
            return SocialUserModel.objects
                .get()
                .action("remove_social_users", {
                    social_user_ids: JSON.stringify(toDelete),
                    entity_id: JSON.stringify(entityId),
                    qdt: JSON.stringify(quorumDataTypeValue),
                })
                .GET()
        }

        return Promise.resolve({})
    }

    return Promise.all([addUsers(), deleteUsers()])
}
